import { useEffect, useState, memo, useMemo, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem,
  FormHelperText, Snackbar, Alert, CircularProgress,
  FormControlLabel, Radio, RadioGroup, Paper, ClickAwayListener
} from '@mui/material';
import { Close, Payment, Print } from '@mui/icons-material';

const PRIMARY = '#5c3d1e';
const PRIMARY_DARK = '#3b2410';

const fs = {
  '& .MuiOutlinedInput-root': { borderRadius: '4px', height: '32px' },
  '& .MuiInputLabel-root': { fontSize: '13px' },
  '& .MuiInputBase-input': { padding: '6px 10px', fontSize: '13px' },
};

// AcType: 1=Master, 2=AccountHead, 3=Bank
const ACTYPE = { Master: 1, AccountHead: 2, Bank: 3 };
const ACTYPE_R = { 1: 'Master', 2: 'AccountHead', 3: 'Bank' };

const EMPTY = {
  Id: 0,
  FinYear: '',
  BillNo: '',
  BillDate: '',
  IsTypeReceipt: false, // false=Payment, true=Receipt
  AcType: 1,
  MasterId: null,
  AccountHeadId: null,
  BankId: null,       // master bank (when AcType=3)
  Amount: '',
  IsCancelled: false,
  PaymentMode: 1,     // 1=Cash, 2=Cheque, 3=Online
  ChequeNo: '',
  CheqDate: '',
  CheqBankId: null,   // bank where cheque deposited
  Remarks: '',
};

// Static sx objects outside component to prevent recreation on every render
const sxDialogTitle = { py: 1, px: 1.5, background: `linear-gradient(135deg, ${PRIMARY_DARK}, ${PRIMARY})`, color: 'white' };
const sxDialogContent = { p: 1.5, background: '#fdf6ec' };
const sxInnerBox = { background: '#fff', borderRadius: '8px', p: 1.5, border: '1px solid #f0e8d8' };
const sxActions = { px: 1.5, py: 1, background: '#f8f9fa', borderTop: '1px solid #e9ecef', justifyContent: 'space-between' };
const sxRemarks = {
  '& .MuiInputLabel-root': { fontSize: '13px' },
  '& .MuiInputBase-input': { fontSize: '13px', padding: '6px 10px' },
  '& .MuiOutlinedInput-root': { borderRadius: '4px' },
};
const sxBankBox = { mt: 1.5, p: 1.5, border: '1px solid #e5e7eb', borderRadius: '4px' };

// Searchable dropdown — renders only filtered items, no lag with large lists
const SearchableSelect = memo(({ label, value, options, onChange, onBlur, error, helperText, disabled }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedLabel = options.find(o => o.Id === value)?.Name || '';

  const filtered = useMemo(() => {
    if (!search.trim()) return options.slice(0, 50); // show first 50 when no search
    const q = search.toLowerCase();
    return options.filter(o => o.Name?.toLowerCase().includes(q)).slice(0, 50);
  }, [options, search]);

  const handleSelect = (id, name) => {
    onChange(id);
    setSearch('');
    setOpen(false);
  };

  const handleOpen = () => {
    if (!disabled) {
      setSearch('');
      setOpen(true);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => { setOpen(false); setSearch(''); if (onBlur) onBlur(); }}>
      <Box ref={containerRef} sx={{ position: 'relative' }}>
        <TextField
          fullWidth size="small"
          label={label}
          value={open ? search : selectedLabel}
          onChange={e => setSearch(e.target.value)}
          onClick={handleOpen}
          onFocus={handleOpen}
          disabled={disabled}
          error={error}
          helperText={helperText}
          placeholder={open ? 'Type to search...' : ''}
          autoComplete="off"
          sx={{
            ...fs,
            '& .MuiInputBase-input': { padding: '6px 10px', fontSize: '13px', cursor: disabled ? 'default' : 'pointer' },
          }}
        />
        {open && (
          <Paper elevation={4} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1400, maxHeight: 220, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px', mt: '2px' }}>
            {filtered.length === 0 ? (
              <Box sx={{ p: 1.5, color: '#999', fontSize: '13px', textAlign: 'center' }}>No results found</Box>
            ) : (
              filtered.map(o => (
                <Box
                  key={o.Id}
                  onMouseDown={e => { e.preventDefault(); handleSelect(o.Id, o.Name); }}
                  sx={{
                    px: 1.5, py: 0.75, fontSize: '13px', cursor: 'pointer',
                    backgroundColor: o.Id === value ? `rgba(92,61,30,0.08)` : 'transparent',
                    fontWeight: o.Id === value ? 600 : 400,
                    '&:hover': { backgroundColor: 'rgba(92,61,30,0.06)' },
                  }}
                >
                  {o.Name}
                </Box>
              ))
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
});

const PaymentReceiptsModal = memo(({
  open,
  onClose,
  modalAction,
  initialData,
  onSave,
  dropdownData,    // { Master: [], AccountHead: [], Bank: [] } — pre-loaded from Index
  banks,
  paymentMethods,
  saving,
  fetchMaxBillNo,
  onPrint,
}) => {
  const [form, setForm] = useState(EMPTY);
  const [headType, setHeadType] = useState('Master');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const isView = modalAction === 'view';
  const isReceipt = !!form.IsTypeReceipt;

  // No data fetching needed — dropdownData comes from Index

  // Init form on open
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setTouched({});

    if (modalAction === 'create') {
      const today = new Date().toISOString().split('T')[0];
      setForm({ ...EMPTY, BillDate: today });
      setHeadType('Master');
      if (fetchMaxBillNo) {
        fetchMaxBillNo(false).then(d => { // false = Payment
          setForm(prev => ({ ...prev, BillNo: d.billNumber || '' }));
        }).catch(() => {});
      }
    } else if (initialData) {
      setForm({
        ...EMPTY,
        ...initialData,
        BillDate: initialData.BillDate ? initialData.BillDate.split('T')[0] : '',
        CheqDate: initialData.CheqDate ? initialData.CheqDate.split('T')[0] : '',
      });
      setHeadType(ACTYPE_R[initialData.AcType] || 'Master');
    }
  }, [open, modalAction]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleHeadTypeChange = (type) => {
    setHeadType(type);
    setForm(prev => ({
      ...prev,
      AcType: ACTYPE[type],
      MasterId: null,
      AccountHeadId: null,
      BankId: null,
    }));
  };

  const handleMasterChange = (value) => {
    const id = parseInt(value);
    setForm(prev => ({
      ...prev,
      AcType: ACTYPE[headType],
      MasterId: headType === 'Master' ? id : null,
      AccountHeadId: headType === 'AccountHead' ? id : null,
      BankId: headType === 'Bank' ? id : null,
    }));
    if (errors.master) setErrors(prev => ({ ...prev, master: '' }));
  };

  const handleTypeChange = (val) => {
    const isR = val === 'receipt';
    set('IsTypeReceipt', isR);
    if (fetchMaxBillNo && modalAction === 'create') {
      fetchMaxBillNo(isR).then(d => {
        setForm(prev => ({ ...prev, IsTypeReceipt: isR, BillNo: d.billNumber || '' }));
      }).catch(() => {});
    }
  };

  const handlePaymentModeChange = (val) => {
    const v = parseInt(val);
    setForm(prev => ({
      ...prev,
      PaymentMode: v,
      ...(v === 1 ? { ChequeNo: '', CheqDate: '', CheqBankId: null } : {}),
    }));
  };

  // Get the currently selected master id (whichever field is set)
  const selectedMasterId = form.MasterId || form.AccountHeadId || form.BankId || null;

  const validate = (field, value) => {
    if (field === 'BillNo' && (!value || !String(value).trim())) return 'Required';
    if (field === 'BillDate' && !value) return 'Required';
    if (field === 'master' && !selectedMasterId) return 'Required';
    if (field === 'Amount' && (!value || parseFloat(value) <= 0)) return 'Must be > 0';
    return '';
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const val = field === 'master' ? selectedMasterId : form[field];
    setErrors(prev => ({ ...prev, [field]: validate(field, val) }));
  };

  const handleSave = () => {
    const errs = {};
    ['BillNo', 'BillDate', 'master', 'Amount'].forEach(f => {
      const val = f === 'master' ? selectedMasterId : form[f];
      const e = validate(f, val);
      if (e) errs[f] = e;
    });
    setTouched({ BillNo: true, BillDate: true, master: true, Amount: true });
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setSnackbar({ open: true, message: Object.values(errs).join(' | ') });
      return;
    }
    onSave(form);
  };

  const currentList = (dropdownData && dropdownData[headType]) || [];
  const selectedName = currentList.find(m => m.Id === selectedMasterId)?.Name || '-';

  // Memoize bank and payment method items (small lists, fine to memo)
  const bankMenuItems = useMemo(() => (banks || []).map(b => (
    <MenuItem key={b.Id} value={b.Id}>{b.Name}</MenuItem>
  )), [banks]);

  const paymentMethodItems = useMemo(() => {
    if (paymentMethods && paymentMethods.length > 0) {
      return paymentMethods.filter(p => p.IsActive !== false).map(p => (
        <MenuItem key={p.Id} value={p.Id}>{p.Name}</MenuItem>
      ));
    }
    return [
      <MenuItem key={1} value={1}>Cash</MenuItem>,
      <MenuItem key={2} value={2}>Cheque</MenuItem>,
      <MenuItem key={3} value={3}>Online Transfer</MenuItem>,
    ];
  }, [paymentMethods]);

  const title = modalAction === 'create'
    ? (isReceipt ? 'Create Receipt' : 'Create Payment')
    : modalAction === 'edit'
      ? (isReceipt ? 'Edit Receipt' : 'Edit Payment')
      : (isReceipt ? 'View Receipt' : 'View Payment');

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '8px', maxWidth: '900px' } }}>

        {/* Header */}
        <DialogTitle sx={sxDialogTitle}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Payment sx={{ fontSize: 18 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{title}</Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: 'white', p: '2px' }}>
              <Close style={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={sxDialogContent}>
          <Box sx={sxInnerBox}>
            <Typography sx={{ fontWeight: 600, color: PRIMARY, mb: 1.5, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'Times New Roman' }}>
              {isReceipt ? 'Receipt Information' : 'Payment Information'}
            </Typography>

            <Grid container spacing={1.5}>

              {/* Bill No */}
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small"
                  label={isReceipt ? 'Receipt No *' : 'Voucher No *'}
                  value={form.BillNo}
                  onChange={e => set('BillNo', e.target.value)}
                  onBlur={() => handleBlur('BillNo')}
                  disabled={isView}
                  error={touched.BillNo && !!errors.BillNo}
                  helperText={touched.BillNo && errors.BillNo}
                  sx={fs}
                />
              </Grid>

              {/* Bill Date */}
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small" type="date"
                  label={isReceipt ? 'Receipt Date *' : 'Voucher Date *'}
                  value={form.BillDate}
                  onChange={e => set('BillDate', e.target.value)}
                  onBlur={() => handleBlur('BillDate')}
                  disabled={isView}
                  error={touched.BillDate && !!errors.BillDate}
                  helperText={touched.BillDate && errors.BillDate}
                  InputLabelProps={{ shrink: true }}
                  sx={fs}
                />
              </Grid>

              {/* Type: Payment / Receipt */}
              <Grid item xs={6} sm={1.5}>
                <FormControl fullWidth size="small" sx={fs}>
                  <InputLabel>Type *</InputLabel>
                  <Select value={isReceipt ? 'receipt' : 'payment'} onChange={e => handleTypeChange(e.target.value)} label="Type *" disabled={isView}>
                    <MenuItem value="payment">Payment</MenuItem>
                    <MenuItem value="receipt">Receipt</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Payment Mode */}
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small" sx={fs}>
                  <InputLabel>Payment Mode</InputLabel>
                  <Select value={form.PaymentMode || 1} onChange={e => handlePaymentModeChange(e.target.value)} label="Payment Mode" disabled={isView}>
                    {paymentMethodItems}
                  </Select>
                </FormControl>
              </Grid>

              {/* AcType radio (create/edit only) */}
              {!isView && (
                <Grid item xs={12} sm={4.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '32px' }}>
                    <RadioGroup row value={headType} onChange={e => handleHeadTypeChange(e.target.value)} sx={{ gap: 0.5, flexWrap: 'nowrap' }}>
                      {['Master', 'AccountHead', 'Bank'].map(t => (
                        <FormControlLabel key={t} value={t}
                          control={<Radio size="small" sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY }, p: '2px' }} />}
                          label={<Typography sx={{ fontSize: '11px', fontWeight: 500 }}>{t === 'AccountHead' ? 'Account Head' : t}</Typography>}
                          sx={{ margin: 0 }}
                        />
                      ))}
                    </RadioGroup>
                  </Box>
                </Grid>
              )}

              {/* Master / AccountHead / Bank selector */}
              <Grid item xs={12} sm={isView ? 5.5 : 5.5}>
                {isView ? (
                  <TextField fullWidth size="small" label={headType === 'AccountHead' ? 'Account Head' : headType} value={selectedName} disabled sx={fs} />
                ) : (
                  <Box>
                    <SearchableSelect
                      label={headType === 'AccountHead' ? 'Account Head *' : `${headType} *`}
                      value={selectedMasterId}
                      options={currentList}
                      onChange={handleMasterChange}
                      onBlur={() => handleBlur('master')}
                      error={touched.master && !!errors.master}
                      helperText={touched.master && errors.master}
                      disabled={false}
                    />
                  </Box>
                )}
              </Grid>

              {/* Amount */}
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small" type="number"
                  label="Amount *"
                  value={form.Amount}
                  onChange={e => set('Amount', e.target.value)}
                  onBlur={() => handleBlur('Amount')}
                  disabled={isView}
                  error={touched.Amount && !!errors.Amount}
                  helperText={touched.Amount && errors.Amount}
                  inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                  sx={fs}
                />
              </Grid>



              {/* Remarks */}
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="Remarks"
                  value={form.Remarks || ''}
                  onChange={e => set('Remarks', e.target.value)}
                  disabled={isView}
                  inputProps={{ maxLength: 100 }}
                  sx={sxRemarks}
                />
              </Grid>
            </Grid>

            {/* Cheque / Online Details */}
            {form.PaymentMode !== 1 && (
              <Box sx={sxBankBox}>
                <Typography sx={{ fontWeight: 600, color: PRIMARY, mb: 1, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'Times New Roman' }}>
                  {form.PaymentMode === 2 ? 'Cheque Details' : 'Transaction Details'}
                </Typography>
                <Grid container spacing={1.5}>
                  {/* ChequeNo */}
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small"
                      label={form.PaymentMode === 2 ? 'Cheque No' : 'Reference No'}
                      value={form.ChequeNo || ''}
                      onChange={e => set('ChequeNo', e.target.value)}
                      disabled={isView}
                      inputProps={{ maxLength: 20 }}
                      sx={fs}
                    />
                  </Grid>

                  {/* CheqDate */}
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" type="date"
                      label={form.PaymentMode === 2 ? 'Cheque Date' : 'Transaction Date'}
                      value={form.CheqDate || ''}
                      onChange={e => set('CheqDate', e.target.value)}
                      disabled={isView}
                      InputLabelProps={{ shrink: true }}
                      sx={fs}
                    />
                  </Grid>

                  {/* CheqBankId — bank where cheque is deposited */}
                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth size="small" sx={fs}>
                      <InputLabel>{form.PaymentMode === 2 ? 'Deposited Bank' : 'Bank'}</InputLabel>
                      <Select
                        value={form.CheqBankId || ''}
                        onChange={e => set('CheqBankId', e.target.value ? parseInt(e.target.value) : null)}
                        label={form.PaymentMode === 2 ? 'Deposited Bank' : 'Bank'}
                        disabled={isView}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 220 } } }}
                      >
                        <MenuItem value=""><em>Select Bank</em></MenuItem>
                        {bankMenuItems}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={sxActions}>
          <Box>
            {(modalAction === 'view' || modalAction === 'edit') && form.Id && onPrint && (
              <Button onClick={() => onPrint(form.Id)} variant="contained" size="small" startIcon={<Print />}
                style={{ background: 'linear-gradient(135deg, #c9a84c, #9a7a2e)', color: 'white', textTransform: 'none', fontWeight: 600, fontSize: '12px' }}>
                Download PDF
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} style={{ color: '#6c757d', textTransform: 'none', fontWeight: 500, fontSize: '12px' }}>
              Cancel
            </Button>
            {!isView && (
              <Button onClick={handleSave} variant="contained" disabled={saving}
                style={{ background: 'linear-gradient(135deg, #c9a84c, #9a7a2e)', color: 'white', textTransform: 'none', fontWeight: 600, fontSize: '12px', borderRadius: '4px' }}>
                {saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : modalAction === 'create' ? 'Save' : 'Update'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setSnackbar(p => ({ ...p, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
});

export default PaymentReceiptsModal;
